import { Box, Divider, IconButton, Stack } from "@mui/material";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { AuthUser, humanize } from "@/auth/helpers";
import { useUser } from "@/auth/useUser";
import WorkShiftsTracker from "@/components/serviceFlow/clockin/workShiftsTracker/workShiftsTracker";
import { GREY } from "@/config/mui/colorPalette";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { getDefaultRoute } from "@/utils/getDefaultRoute";
import { MessageIcon, SearchIcon, WarningCircleIcon } from "../icons";
import SwitchAccountIconSvg from "../icons/switchAccountIconSvg";
import IntercomCustomLauncher from "../intercom/intercomCustomLauncher";
import RadioModal from "../sorting/radioModal";
import MessagesNotificationsDot from "./messagesNotificationsDot";
import TopBarMobileMenu from "./topBarMobileMenu";
import TopBarUserBanner from "./topBarUserBanner";

function TopBarActions({
  user,
  onCloseNavBar,
}: {
  user: AuthUser;
  onCloseNavBar: () => void;
}) {
  const [accountSwitchModalOpen, setAccountSwitchModalOpen] = useState(false);
  const { commandAiEnabled, intercomV1Enabled, clockInClockOutV1Enabled } =
    useFeatureFlags();
  const router = useRouter();
  const pathName = usePathname();
  const {
    medspa,
    userMedspa,
    additionalUserDetails,
    onMedspaChange,
    hasFeaturePermission,
    hasUrlPermission,
  } = useUser();
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const switchableMedspas = useMemo(() => {
    return additionalUserDetails?.userMedspa ?? [];
  }, [additionalUserDetails]);

  const canSwitchRoles = hasFeaturePermission(
    FeaturePermission.HAVE_MULTIPLE_USERMEDSPA
  );

  const canTrackWorkShifts = hasFeaturePermission(
    FeaturePermission.TRACK_WORK_SHIFTS
  );

  const enableMedspaSwitch = canSwitchRoles && switchableMedspas.length > 1;

  // Providers can only belong to one medspa, so it's not necessary to show the user banner
  const messagesUrl = `/${medspa}/messages`;
  const canViewMessages = hasUrlPermission(messagesUrl);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        p: 0,
        m: 0,
        mr: 1,
      }}
    >
      {canSwitchRoles && (
        <>
          <TopBarUserBanner
            medspaName={userMedspa?.medspa.name ?? ""}
            userRole={humanize(user.newRole)}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 1 }}
          />
        </>
      )}
      {!isMobile && clockInClockOutV1Enabled && canTrackWorkShifts && (
        <>
          <WorkShiftsTracker />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 0.5 }}
          />
        </>
      )}
      {/* Global Search Bar */}
      {commandAiEnabled &&
        typeof window !== "undefined" &&
        window.CommandBar && (
          <IconButton
            onClick={() => {
              window.CommandBar.open();
              onCloseNavBar();
            }}
          >
            <SearchIcon color={GREY[70]} size={22} />
          </IconButton>
        )}
      {!isMobile && enableMedspaSwitch && (
        <IconButton
          onClick={() => {
            setAccountSwitchModalOpen(true);
          }}
        >
          <SwitchAccountIconSvg />
        </IconButton>
      )}
      {isMobile && canSwitchRoles && (
        <>
          {clockInClockOutV1Enabled && canTrackWorkShifts && (
            <WorkShiftsTracker showAdditionalInfo={false} />
          )}
          <TopBarMobileMenu
            medspa={medspa}
            enableMedspaSwitch={enableMedspaSwitch}
            enableMessages={canViewMessages}
            onClickSwitchAccount={setAccountSwitchModalOpen}
          />
        </>
      )}
      {(!isMobile || canViewMessages) && !canSwitchRoles && (
        <>
          {!intercomV1Enabled && (
            <IconButton
              onClick={() => {
                window.birdeatsbug.trigger();
                onCloseNavBar();
              }}
            >
              <WarningCircleIcon color={GREY[70]} size={22} />
            </IconButton>
          )}
          {canViewMessages && (
            <IconButton href={messagesUrl}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <MessageIcon color={GREY[70]} size={22} />
                <MessagesNotificationsDot />
              </Box>
            </IconButton>
          )}
        </>
      )}
      {isMobile &&
        !canSwitchRoles &&
        clockInClockOutV1Enabled &&
        canTrackWorkShifts && (
          <>
            <WorkShiftsTracker showAdditionalInfo={false} />
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 0.5 }}
            />
          </>
        )}
      {enableMedspaSwitch && (
        <RadioModal
          buttonLabel="Select Medspa"
          options={switchableMedspas.map((userMedspa) => ({
            ...userMedspa,
            id: userMedspa.medspa.id,
            label: userMedspa.medspa.name,
            image: userMedspa.medspa.logo,
          }))}
          activeOptionId={medspa}
          showLogos
          onOptionChange={async (selectedOption) => {
            const newMedspaID = selectedOption.id;

            if (medspa !== newMedspaID) {
              onMedspaChange(newMedspaID);

              const userMedspa =
                selectedOption as unknown as (typeof switchableMedspas)[number];
              const isComplianceHub = pathName.includes("compliance-hub");

              const nextRoute = isComplianceHub
                ? `/${newMedspaID}/compliance-hub`
                : getDefaultRoute(userMedspa);

              router.push(nextRoute);
            }
          }}
          onClose={() => setAccountSwitchModalOpen(false)}
          open={accountSwitchModalOpen}
          title="Switch Accounts"
        />
      )}
      {intercomV1Enabled && <IntercomCustomLauncher />}
    </Stack>
  );
}

export default TopBarActions;
